import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Button, Text, Input, Spinner,
  makeStyles, tokens,
  MessageBar, MessageBarBody,
} from '@fluentui/react-components'
import { ArrowLeft24Regular } from '@fluentui/react-icons'
import { meService } from '../services/meService'

const useStyles = makeStyles({
  page:      { maxWidth: '720px' },
  card:      { backgroundColor: tokens.colorNeutralBackground1, borderRadius: tokens.borderRadiusMedium, padding: '24px', boxShadow: tokens.shadow4, marginBottom: '16px' },
  sectionTitle: {
    fontSize: tokens.fontSizeBase400, fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    paddingBottom: '10px', borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    marginBottom: '20px',
  },
  fieldRow:  { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '16px' },
  fieldLabel: {
    fontSize: '11px', fontWeight: 600, textTransform: 'uppercase' as const,
    letterSpacing: '.06em', color: tokens.colorNeutralForeground3,
    marginBottom: '6px', display: 'block',
  },
  floatingBar: {
    position: 'sticky' as const,
    bottom: '0',
    backgroundColor: tokens.colorNeutralBackground1,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    padding: '12px 0',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginTop: '24px',
    zIndex: 10,
  },
})

export default function MyProfileEditPage() {
  const styles = useStyles()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [form, setForm] = useState({
    personalEmail:    '',
    personalPhone:    '',
    nextOfKinName:    '',
    nextOfKinAddress: '',
    nextOfKinEmail:   '',
    nextOfKinPhone:   '',
  })
  const [error, setError] = useState('')

  const { data: profile, isLoading } = useQuery({
    queryKey: ['me'],
    queryFn: meService.getMe,
  })

  useEffect(() => {
    if (profile) {
      setForm({
        personalEmail:    profile.personalEmail    ?? '',
        personalPhone:    profile.personalPhone    ?? '',
        nextOfKinName:    profile.nextOfKinName    ?? '',
        nextOfKinAddress: profile.nextOfKinAddress ?? '',
        nextOfKinEmail:   profile.nextOfKinEmail   ?? '',
        nextOfKinPhone:   profile.nextOfKinPhone   ?? '',
      })
    }
  }, [profile])

  const saveMutation = useMutation({
    mutationFn: () => meService.updatePrivate({
      personalEmail:    form.personalEmail    || undefined,
      personalPhone:    form.personalPhone    || undefined,
      nextOfKinName:    form.nextOfKinName    || undefined,
      nextOfKinAddress: form.nextOfKinAddress || undefined,
      nextOfKinEmail:   form.nextOfKinEmail   || undefined,
      nextOfKinPhone:   form.nextOfKinPhone   || undefined,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['me'] })
      navigate('/my-profile')
    },
    onError: () => setError('Uložení se nezdařilo. Zkuste to prosím znovu.'),
  })

  const set = (field: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm(f => ({ ...f, [field]: e.target.value }))

  if (isLoading) return <Spinner label="Načítání..." />

  return (
    <div className={styles.page}>
      {/* Záhlaví */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '24px' }}>
        <Button appearance="subtle" icon={<ArrowLeft24Regular />} onClick={() => navigate('/my-profile')} />
        <Text size={600} weight="semibold">Upravit soukromé kontaktní údaje</Text>
      </div>

      <div className={styles.card}>
        <div className={styles.sectionTitle}>Soukromý kontakt</div>

        <div className={styles.fieldRow}>
          <div>
            <span className={styles.fieldLabel}>Osobní e-mail</span>
            <Input
              value={form.personalEmail}
              onChange={set('personalEmail')}
              type="email"
              placeholder="jan.novak@email.cz"
            />
          </div>
          <div>
            <span className={styles.fieldLabel}>Osobní telefon</span>
            <Input
              value={form.personalPhone}
              onChange={set('personalPhone')}
              type="tel"
              placeholder="+420 777 123 456"
            />
          </div>
        </div>

        <div className={styles.sectionTitle} style={{ marginTop: 24 }}>Osoba blízká</div>

        <div className={styles.fieldRow}>
          <div>
            <span className={styles.fieldLabel}>Jméno</span>
            <Input value={form.nextOfKinName} onChange={set('nextOfKinName')} />
          </div>
          <div>
            <span className={styles.fieldLabel}>Adresa</span>
            <Input value={form.nextOfKinAddress} onChange={set('nextOfKinAddress')} />
          </div>
        </div>
        <div className={styles.fieldRow}>
          <div>
            <span className={styles.fieldLabel}>E-mail</span>
            <Input value={form.nextOfKinEmail} onChange={set('nextOfKinEmail')} type="email" />
          </div>
          <div>
            <span className={styles.fieldLabel}>Telefon</span>
            <Input value={form.nextOfKinPhone} onChange={set('nextOfKinPhone')} type="tel" />
          </div>
        </div>
      </div>

      {error && (
        <MessageBar intent="error" style={{ marginTop: 16 }}>
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      {/* Floating bar */}
      <div className={styles.floatingBar}>
        <Button appearance="subtle" onClick={() => navigate('/my-profile')} disabled={saveMutation.isPending}>
          Zrušit
        </Button>
        <Button appearance="primary" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
          {saveMutation.isPending ? 'Ukládám...' : 'Uložit'}
        </Button>
      </div>
    </div>
  )
}
